import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { Plus, Trash2, Edit3, DollarSign } from "lucide-react";

const PRICING_ITEMS = [
  { key: "sites", label: "Additional Site" },
  { key: "users", label: "Additional User" },
  { key: "equipment", label: "Additional Equipment / site" },
  { key: "facilities", label: "Additional Facility / site" },
  { key: "daily_kitchen_record", label: "Daily Kitchen Record" },
];

const DEFAULT_FEATURES = {
  max_sites: 1, max_users: 5, max_ccps_per_site: 5, max_facilities_per_site: 5,
  ai_suggestions_enabled: false, white_label_enabled: false,
  knowledge_base_enabled: false, pdf_reports_enabled: false,
  calibration_tracking_enabled: true, email_alerts_enabled: false,
  priority_support: false, daily_kitchen_record: false,
};

const FEATURE_TOGGLES = [
  { k: "email_alerts_enabled", label: "Email alerts for deviations" },
  { k: "priority_support", label: "Priority support" },
  { k: "knowledge_base_enabled", label: "AI Knowledge Base" },
  { k: "daily_kitchen_record", label: "Daily Kitchen Record" },
];

export default function Tiers() {
  const { confirm, notify } = useDialog();
  const [tiers, setTiers] = useState([]);
  const [show, setShow] = useState(null); // null | "create" | tier
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/tiers"); setTiers(data.tiers || []); };
  const loadPricing = async () => {
    try { const { data } = await api.get("/admin/addon-pricing"); setPricing(data.pricing?.prices || null); }
    catch (e) { console.debug("pricing load failed", e); }
  };
  useEffect(() => { load(); loadPricing(); }, []);

  const setPrice = (item, field) => (e) =>
    setPricing((p) => ({ ...p, [item]: { ...(p?.[item] || {}), [field]: e.target.value } }));

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await api.put("/admin/addon-pricing", { prices: pricing });
      await notify({ title: "Add-on pricing saved", variant: "ok" });
      await loadPricing();
    } catch (e) {
      await notify({ title: "Couldn't save pricing", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setSavingPricing(false); }
  };

  const openCreate = () => { setForm({ name: "", description: "", price_monthly: 0, trial_days: 0, is_active: true, features: { ...DEFAULT_FEATURES } }); setShow("create"); setErr(""); };
  const openEdit = (t) => { setForm({ ...t, features: { ...DEFAULT_FEATURES, ...t.features } }); setShow(t); setErr(""); };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    const payload = {
      name: form.name, description: form.description || "",
      price_monthly: Number(form.price_monthly) || 0, trial_days: Number(form.trial_days) || 0,
      is_active: !!form.is_active,
      features: {
        ...form.features,
        max_sites: Number(form.features.max_sites) || 1,
        max_users: Number(form.features.max_users) || 1,
        max_ccps_per_site: Number(form.features.max_ccps_per_site) || 1,
        max_facilities_per_site: Number(form.features.max_facilities_per_site) || 1,
      },
    };
    try {
      if (show === "create") await api.post("/admin/tiers", payload);
      else await api.put(`/admin/tiers/${form.id}`, payload);
      setShow(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!await confirm({ title: `Delete tier ${t.name}?`, message: "Tenants currently on this tier will need to be reassigned first.", danger: true, confirmLabel: "Delete tier" })) return;
    try { await api.delete(`/admin/tiers/${t.id}`); load(); }
    catch (e) { await notify({ title: "Couldn't delete", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const updF = (k) => (e) => setForm({ ...form, features: { ...form.features, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value } });

  return (
    <div className="vb-fade-in" data-testid="tiers-page">
      <div className="flex justify-between mb-6 flex-wrap gap-3">
        <div><h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>Subscription tiers</h1>
          <p className="opacity-70">Define the plans your customers can subscribe to. Feature toggles control what each tier gets — limits are enforced automatically.</p></div>
        <button className="vb-btn" onClick={openCreate} data-testid="add-tier"><Plus size={16} /> New tier</button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {tiers.map((t) => (
          <div key={t.id} className="vb-card p-5" data-testid={`tier-card-${t.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="vb-heading text-2xl" style={{ color: "var(--vb-text-2)" }}>{t.name}</h3>
                <div className="text-sm opacity-70">{t.description || "—"}</div>
              </div>
              {t.is_active ? <span className="vb-pill ok">active</span> : <span className="vb-pill">inactive</span>}
            </div>
            <div className="my-3 vb-heading text-3xl" style={{ color: "var(--vb-text)" }}>
              ${t.price_monthly}<span className="text-sm opacity-60 font-normal"> /month</span>
            </div>
            <ul className="text-sm space-y-1 mb-4">
              <li>· {t.features.max_sites} sites</li>
              <li>· {t.features.max_users} user seats</li>
              <li>· {t.features.max_ccps_per_site} equipment per site</li>
              <li>· {t.features.max_facilities_per_site ?? DEFAULT_FEATURES.max_facilities_per_site} facilities per site</li>
              {FEATURE_TOGGLES.map((f) => (
                <li key={f.k} className={t.features[f.k] ? "" : "opacity-40 line-through"}>
                  {t.features[f.k] ? "✓" : "✗"} {f.label}
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button className="vb-btn ghost flex-1" onClick={() => openEdit(t)} data-testid={`edit-tier-${t.id}`}><Edit3 size={14} /> Edit</button>
              <button className="vb-btn ghost" onClick={() => del(t)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {pricing && (
        <div className="vb-card p-5 mt-6" data-testid="addon-pricing-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
            <div className="flex items-center gap-2">
              <DollarSign size={18} style={{ color: "var(--vb-text-2)" }} />
              <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Add-on unit pricing (SGD)</h2>
            </div>
            <button className="vb-btn" onClick={savePricing} disabled={savingPricing} data-testid="save-pricing">
              {savingPricing ? "Saving…" : "Save pricing"}
            </button>
          </div>
          <p className="opacity-70 text-sm mb-4">Default per-unit prices sales can adjust per customer in each tenant's Package & Pricing.</p>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider opacity-50 px-1">
              <div className="col-span-6">Item</div>
              <div className="col-span-3">Monthly</div>
              <div className="col-span-3">Yearly</div>
            </div>
            {PRICING_ITEMS.map((it) => (
              <div key={it.key} className="grid grid-cols-12 gap-2 items-center" data-testid={`pricing-row-${it.key}`}>
                <div className="col-span-6 text-sm">{it.label}</div>
                <div className="col-span-3">
                  <input className="vb-input" type="number" min="0" step="0.01"
                    value={pricing[it.key]?.monthly ?? 0} onChange={setPrice(it.key, "monthly")}
                    data-testid={`pricing-${it.key}-monthly`} />
                </div>
                <div className="col-span-3">
                  <input className="vb-input" type="number" min="0" step="0.01"
                    value={pricing[it.key]?.yearly ?? 0} onChange={setPrice(it.key, "yearly")}
                    data-testid={`pricing-${it.key}-yearly`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {show && (<div className="vb-modal-bg"><div className="vb-modal" style={{ maxWidth: 720 }}>
        <h2>{show === "create" ? "Create tier" : `Edit ${form.name}`}</h2>
        {err && <div className="vb-pill err mb-3">{err}</div>}
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase opacity-70">Tier name *</label>
              <input className="vb-input" required placeholder="e.g. Pro" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tier-name" />
            </div>
            <div>
              <label className="text-xs uppercase opacity-70">Price / month ($)</label>
              <input className="vb-input" type="number" step="0.01" placeholder="0.00" value={form.price_monthly || 0} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} data-testid="tier-price" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase opacity-70">Description</label>
            <input className="vb-input" placeholder="Short summary shown to customers" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tier-description" />
          </div>
          <div>
            <label className="text-xs uppercase opacity-70">Default trial days (0 = none)</label>
            <input className="vb-input" type="number" placeholder="0" value={form.trial_days || 0} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} data-testid="tier-trial-days" />
          </div>

          <div className="border-t pt-3 mt-1"><strong className="vb-heading" style={{ color: "var(--vb-text-2)" }}>Limits</strong></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="text-xs uppercase opacity-70">Max sites</label><input className="vb-input" type="number" value={form.features?.max_sites || 1} onChange={updF("max_sites")} data-testid="tier-max-sites" /></div>
            <div><label className="text-xs uppercase opacity-70">Max users</label><input className="vb-input" type="number" value={form.features?.max_users || 1} onChange={updF("max_users")} data-testid="tier-max-users" /></div>
            <div><label className="text-xs uppercase opacity-70">Max Equipment / site</label><input className="vb-input" type="number" value={form.features?.max_ccps_per_site || 1} onChange={updF("max_ccps_per_site")} data-testid="tier-max-equipment" /></div>
            <div><label className="text-xs uppercase opacity-70">Facilities / site</label><input className="vb-input" type="number" value={form.features?.max_facilities_per_site || 1} onChange={updF("max_facilities_per_site")} data-testid="tier-max-facilities" /></div>
          </div>

          <div className="border-t pt-3 mt-1"><strong className="vb-heading" style={{ color: "var(--vb-text-2)" }}>Features</strong></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FEATURE_TOGGLES.map((f) => (
              <label key={f.k} className="flex items-center gap-2 text-sm vb-card p-2">
                <input type="checkbox" checked={!!form.features?.[f.k]} onChange={updF(f.k)} data-testid={`feat-${f.k}`} /> {f.label}
              </label>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active (offerable to new customers)</label>
          <div className="flex gap-2 justify-end"><button type="button" className="vb-btn ghost" onClick={() => setShow(null)}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="tier-submit">{busy ? "Saving…" : "Save"}</button></div>
        </form>
      </div></div>)}
    </div>
  );
}
